import {
  useNavigate, useParams, useLocation, useSearchParams,
} from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import useGetTripById from '../../API/hooks/trips/useGetTripById';
import useGetLongOngoingTrips from '../../API/hooks/trips/useGetLongOngoingTrips';
import useGetRole from '../../API/hooks/useGetRole';
import useGetDeviceLocations from '../../API/hooks/maps/useGetDeviceLocations';
import useGetAlerts from '../../API/hooks/alerts/useGetAlerts';
import useGetDeviceByDeviceId from '../../API/hooks/devices/useGetDeviceByDeviceId';
import useGetDriverById from '../../API/hooks/drivers/useGetDriverById';
import useGetVehicleById from '../../API/hooks/vehicles/useGetVehicleById';
import useGetAlertRisks from '../../API/hooks/alerts/useGetAlertsRisks';
import useGetAlertTypes from '../../API/hooks/alerts/useGetAlertsTypes';
import Modal from '../../components/common/Modal/Modal';
import { TripDetails, TripScorePoint } from '../../API/types/Trips';
import Icon from '../../components/common/Icon/Icon';
import { IconKeys } from '../../components/common/Icon/IconKeys';
import DetailsPageSkeleton from '../../components/common/Skeleton/DetailsPageSkeleton';
import Tags from '../../components/common/Tags';
import MapWidget from '../../components/common/Map/Map';
import ScoreTrendWidget from '../../components/common/ScoreTrend/ScoreTrend';
import KnobChartWidget from '../../components/common/KnobChartWidget/KnobChartWidget';
import DoghnutChartWidget from '../../components/common/DoghnutChartWidget/DoghnutChartWidget';
import DetailsWidget from '../../components/common/DetailsWidget/DetailsWidget';
import { Button } from '../../components/ui/Button/Button';
import HeaderWrapper from '../../components/ui/DetailsPageHeaderWrapper/HeaderWrapper';
import {
  getFormattedDateAndTime,
  convertSecToTimeFullLabel,
  getTripDuration,
} from '../../utils/dateUtils';
import S from './TripDetailsStyles';
import { convertMsToKm, generateDistance } from '../../utils/unitsUtils';
import useGetFleetId from '../../API/hooks/useGetFleetId';
import {
  getFilterFields, getSimpleEqQuery, queryFilters, withInclusiveDateStart,
} from '../../utils/queryFilters';
import DetailsPanel from '../../components/DetailsPanel/DetailsPanel';
import CloseTripForm from '../../components/CloseTripForm/CloseTripForm';
import { ThemeType } from '../../theme/themeType';
import ClockCounter from '../../components/common/ClockCounter/ClockCounter';
import DataPendingTag from '../../components/common/DataPendingTag/DataPendingTag';
import {
  AlertRisksDognutConfig, AlertTypesDognutConfig,
} from '../../API/types/Alerts';
import { CalendarItemType } from '../../components/common/DetailsWidget/types';
import { withScrollRestoreState } from '../../utils/scrollRestoration';
import useDetailsListNavigation from '../../components/common/DetailsListNavigation/DetailsListNavigation';
import { entityMatchingId } from '../../utils/entityMatching';

type TripDetailsLocationState = {
  isMergedTrip?: boolean;
  from?: string;
  alertDetailsState?: unknown;
};

const getStartLocationLabel = (trip: TripDetails): string => (
  trip?.startLocationName || trip?.startLocation?.coordinates?.join(', ') || 'N/A'
);
const getEndLocationLabel = (trip: TripDetails): string => (
  trip?.endLocationName || trip?.endLocation?.coordinates?.join(', ') || 'N/A'
);

function TripDetailsPage(): JSX.Element {
  const theme = useTheme() as ThemeType;
  const fleetId = useGetFleetId();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab');
  const isWideMapTab = !activeTab || activeTab === 'calendar' || activeTab === 'alert';
  const [closeTripModalOpen, toggleCloseTripModalOpen] = useState(false);
  const { id: tripId } = useParams();
  const listNavigation = useDetailsListNavigation({
    entity: 'trips',
    currentId: tripId,
  });
  const { isAdmin, isFleetAdmin, isFleetManager } = useGetRole();
  const canCloseOngoingTrips = isAdmin || isFleetAdmin || isFleetManager;
  const isMergedTripHint = !!(location.state as TripDetailsLocationState | null)?.isMergedTrip;
  const {
    data: trip, isLoading, error, isMergedTrip,
  } = useGetTripById(
    tripId as string,
    fleetId,
    true,
    isMergedTripHint,
  );
  const tripRecordId = trip?._id || tripId;
  const detailEntityId = isMergedTrip
    ? (trip?.mergedTripId || tripRecordId)
    : tripRecordId;
  const shouldPoll = !trip?.endAt;
  const [endTime, setEndTime] = useState<string | undefined>(trip?.endAt || (new Date()).toISOString());
  const tripEndAt = trip?.endAt || endTime || (new Date()).toISOString();
  const mayLookUpClosableTrip = canCloseOngoingTrips && shouldPoll && !!trip && !isMergedTrip;
  const { trips: longOngoingTrips } = useGetLongOngoingTrips(fleetId, mayLookUpClosableTrip);
  const closableTrip = mayLookUpClosableTrip
    ? longOngoingTrips.find((ongoingTrip) => ongoingTrip._id === tripRecordId) || null
    : null;

  const tripFilters = useMemo(() => ({
    period: ['custom'],
    startDate: [trip?.startAt || ''],
    endDate: [tripEndAt],
  }), [trip?.startAt, tripEndAt]);

  const tripDateQuery = trip?.startAt
    ? getFilterFields(tripFilters, ['period'], 'eventDate')
    : '';
  const deviceQuery = trip?.deviceId ? getSimpleEqQuery('deviceId', trip.deviceId, 3) : '';

  const alertsCustomQuery = `${
    isMergedTrip && detailEntityId ? `&tripId=${detailEntityId}` : ''
  }&filter[9][field]=deviceId&filter[9][operator]=$eq&filter[9][value]=${trip?.deviceId}`;
  let alertsQuery = queryFilters({
    startDate: [trip?.startAt],
    endDate: [tripEndAt],
  }, undefined, alertsCustomQuery, 'eventDate');
  const isUnknownOrUnauthorizedDriverTrip = !!(
    trip?.unknownDriverPhotoUrl
    || trip?.unAuthorizedDriverPhotoUrl
    || trip?.driver?.isDummyDriver
  );
  // trip merge
  if (isMergedTrip) {
    alertsQuery = alertsQuery
      .replace(/(\[field]=eventDate&filter\[\d+]\[operator]=\$)gt/g, '$1gte')
      .replace(/(\[field]=eventDate&filter\[\d+]\[operator]=\$)lt/g, '$1lte');
  } else if (isUnknownOrUnauthorizedDriverTrip) {
    alertsQuery = withInclusiveDateStart(alertsQuery);
  }

  const { data: device } = useGetDeviceByDeviceId(trip?.device?.deviceId as string, fleetId, true, shouldPoll);
  const { data: driver } = useGetDriverById(trip?.driver?.driverId as string, fleetId, true, shouldPoll);
  const { data: vehicle } = useGetVehicleById(trip?.vehicle?.vehicleId as string, fleetId, true, shouldPoll);
  const tripDevice = entityMatchingId(device, device?.deviceId, trip?.device?.deviceId);
  const tripDriverDetails = entityMatchingId(driver, driver?._id, trip?.driver?.driverId);
  const tripVehicle = entityMatchingId(vehicle, vehicle?._id, trip?.vehicle?.vehicleId);
  const { data: alerts, isSuccess: alertsLoaded } = useGetAlerts(
    fleetId,
    alertsQuery,
    !!trip?.startAt,
    shouldPoll,
  );
  const { data: risksChartData } = useGetAlertRisks(
    fleetId,
    `?limit=1000&skip=0${deviceQuery}${tripDateQuery}`,
  );
  const { data: typesChartData } = useGetAlertTypes(
    fleetId,
    `?limit=1000&skip=0${deviceQuery}${tripDateQuery}`,
  );

  const alertRisksChartConfig: AlertRisksDognutConfig = useMemo(() => ({
    cards: {
      urgent: { color: theme.charts.alertRiskUrgent, label: 'Urgent', count: risksChartData?.urgent || 0 },
      medium: { color: theme.charts.alertRiskMedium, label: 'Medium', count: risksChartData?.medium || 0 },
      low: { color: theme.charts.alertRiskLow, label: 'Low', count: risksChartData?.low || 0 },
    },
    title: 'Alert risks',
  }), [theme.charts, risksChartData]);

  const alertTypesChartConfig: AlertTypesDognutConfig = useMemo(() => ({
    cards: {
      IMU: { color: theme.charts.alertImu, label: 'IMU', count: typesChartData?.IMU || 0 },
      frontCamera: { color: theme.charts.alertFrontCamera, label: 'Front cam', count: typesChartData?.frontCamera || 0 },
      inwardCamera: { color: theme.charts.alertInwardCamera, label: 'Inw cam', count: typesChartData?.inwardCamera || 0 },
      cloud: { color: theme.charts.alertCloud, label: 'Cloud', count: typesChartData?.cloud || 0 },
    },
    title: 'Alert types',
  }), [theme.charts, typesChartData]);

  const tripScoreTrend = useMemo((): TripScorePoint[] => {
    if (trip?.scoreHistory?.length) {
      return trip.scoreHistory.map((point: TripScorePoint) => ({
        scoreDate: point.scoreDate,
        score: point.score,
      }));
    }
    return [];
  }, [trip?.scoreHistory]);

  const tripScore = trip?.score ?? null;

  const calendarSeedItems = useMemo((): CalendarItemType[] => {
    if (!trip?.startAt || !detailEntityId) {
      return [];
    }

    const items: CalendarItemType[] = [{
      type: 'trip',
      _id: `${tripRecordId}-start`,
      detailsId: detailEntityId,
      date: trip.startAt,
      title: `Trip has started: ${getStartLocationLabel(trip)}`,
      subtitle: getFormattedDateAndTime(trip.startAt),
      vehicle: trip.vehicle?.licensePlateNumber,
      updatedAt: trip.updatedAt as string,
      isMergedTrip: !!isMergedTrip,
    }];

    if (trip.endAt) {
      items.push({
        type: 'trip',
        _id: `${tripRecordId}-end`,
        detailsId: detailEntityId,
        date: trip.endAt,
        title: `Trip has ended: ${getEndLocationLabel(trip)}`,
        subtitle: getFormattedDateAndTime(trip.endAt),
        vehicle: trip.vehicle?.licensePlateNumber,
        updatedAt: trip.updatedAt as string,
        isMergedTrip: !!isMergedTrip,
      });
    }

    return items;
  }, [trip, tripRecordId, detailEntityId, isMergedTrip]);

  const handleBackToList = (): void => navigate('/trips/');

  const handleGoBack = (): void => {
    if (listNavigation.backToList) {
      listNavigation.backToList();
      return;
    }
    const navState = location.state as TripDetailsLocationState | null;
    if (navState?.from) {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate(navState.from, { state: withScrollRestoreState(navState.alertDetailsState) });
      }
      return;
    }
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      handleBackToList();
    }
  };

  const getDriverInfo = (): any => {
    const tripDriver = trip?.driver;
    const resolvedDriver = tripDriverDetails ?? tripDriver ?? null;
    const unknownPhoto = trip?.unknownDriverPhotoUrl;
    const unauthorizedPhoto = trip?.unAuthorizedDriverPhotoUrl;
    const photoUrl = unknownPhoto || unauthorizedPhoto;
    const shouldUseTripPhoto = !!photoUrl
      && (tripDriver == null || tripDriver.isDummyDriver === true);

    if (resolvedDriver && shouldUseTripPhoto) {
      return {
        ...resolvedDriver,
        avatarUrl: photoUrl,
      };
    }

    if (!resolvedDriver && unknownPhoto) {
      return {
        image: unknownPhoto,
        name: 'Unknown driver',
        item: '',
        license: 'N/A',
      };
    }

    if (!resolvedDriver && unauthorizedPhoto) {
      return {
        image: unauthorizedPhoto,
        name: 'Unauthorized driver',
        item: '',
        license: 'N/A',
      };
    }

    return resolvedDriver;
  };

  useEffect(() => {
    if (trip?.endAt) {
      return () => { };
    }
    const intervalId = setInterval(() => {
      if (trip?.endAt) {
        clearInterval(intervalId);
        return;
      }
      setEndTime((new Date()).toISOString());
    }, 2000);
    return () => {
      clearInterval(intervalId);
    };
  }, [trip?.endAt]);

  if (error) {
    handleBackToList();
  }

  return (
    <>
      {isLoading && (<DetailsPageSkeleton />)}

      {!!trip && (
        <S.Wrapper>
          <HeaderWrapper>
            <div className="details-row">
              <div className="overview-container">
                <div className="preview-container">
                  <Button height="44px" padding="10px 12px" onClick={handleGoBack}>
                    <Icon iconKey={IconKeys.ARROW_LEFT} />
                  </Button>
                  <Tags.Trip
                    from={getStartLocationLabel(trip)}
                    to={getEndLocationLabel(trip)}
                  />
                  {listNavigation.controls}
                </div>
              </div>
              <div className="actions-wrapper" />
              <div className="actions-container">
                {!!closableTrip && (
                  <Button
                    height="44px"
                    padding="10px 16px"
                    title="Close this ongoing trip"
                    onClick={() => toggleCloseTripModalOpen(true)}
                  >
                    <Icon iconKey={IconKeys.TIME} />
                    <p>Close trip</p>
                  </Button>
                )}
              </div>
            </div>
            <div className="tags-row">
              {trip.endAt === null && (
                <Tags.Status
                  label="Ongoing"
                  backgroundColor={theme.colors.main_cta}
                />
              )}
              {isMergedTrip && (
                <Tags.Status
                  label="Merged"
                  backgroundColor={theme.colors.green}
                  icon={IconKeys.MERGED_TRIP}
                  width="fit-content"
                />
              )}
              <Tags.LabeledIcon
                flexible
                label={`Trip start at ${getFormattedDateAndTime(trip.startAt)}`}
                icon={IconKeys.TIME}
              />
              {trip.endAt && (
                <Tags.LabeledIcon
                  flexible
                  label={`Trip end at ${getFormattedDateAndTime(trip.endAt)}`}
                  icon={IconKeys.TIME}
                />
              )}
              {trip.endAt && (
                <Tags.LabeledIcon
                  label={getTripDuration(trip.startAt, trip.endAt || (new Date()).toISOString())}
                  icon={IconKeys.TIME}
                />
              )}
              {trip.endAt === null && (tripDevice?.isOnline ? (
                <>
                  <Tags.Label
                    label={`Current speed: ${trip?.speedMs !== null && trip?.speedMs !== undefined ? `${convertMsToKm(trip.speedMs)} km/h` : 'n/a'}`}
                  />
                  <ClockCounter lastTime={trip.startAt} />
                </>
              ) : (
                <DataPendingTag startAt={trip.startAt} />
              ))}
              <Tags.LabeledIcon
                label={generateDistance(trip.distanceInKm)}
                icon={IconKeys.DISTANCE}
              />
              <Tags.Label
                label={`Idle time: ${trip.idleTimeInSec !== undefined ? convertSecToTimeFullLabel(trip.idleTimeInSec, trip.idleTimeInSec > 120) : 'n/a'}`}
              />
              {isMergedTrip && trip.parkedDurationInSec !== undefined && trip.parkedDurationInSec !== null && (
                <Tags.Label
                  label={`Parked time: ${convertSecToTimeFullLabel(trip.parkedDurationInSec, trip.parkedDurationInSec > 120)}`}
                />
              )}
              {isMergedTrip && trip.runningTimeInSec !== undefined && trip.runningTimeInSec !== null && (
                <Tags.Label
                  label={`Running time: ${convertSecToTimeFullLabel(trip.runningTimeInSec, trip.runningTimeInSec > 120)}`}
                />
              )}
              <Tags.LabeledIcon
                label={`${trip.alertCount} ${trip.alertCount === 1 ? 'event' : 'events'}`}
                icon={IconKeys.ALERT_CIRCLE}
              />
              {trip.timeSynchronization === 'NOT_SYNCHRONIZED' && trip.endAt && (
                <Tags.Status
                  label="Trip time might be incorrect"
                  backgroundColor={theme.colors.red}
                />
              )}
              {trip?.startTimestampCorrupted && trip.endAt && (
                <Tags.Status
                  label="Trip start time might be incorrect"
                  backgroundColor={theme.colors.red}
                />
              )}
              {trip?.endTimestampCorrupted && trip.endAt && (
                <Tags.Status
                  label="Trip end time might be incorrect"
                  backgroundColor={theme.colors.red}
                />
              )}
              {trip?.lastActivityAtCorrupted && trip.endAt && (
                <Tags.Status
                  label="Last activity time might be incorrect"
                  backgroundColor={theme.colors.red}
                />
              )}
              {trip?.gpsCorrupted && trip.endAt && (
                <Tags.Status
                  label="GPS location might be incorrect"
                  backgroundColor={theme.colors.red}
                />
              )}
            </div>
          </HeaderWrapper>
          <div className="widgets-container">
            <DetailsWidget
              key={tripId}
              fleetId={fleetId}
              entity="device"
              entityId={trip.deviceId as string}
              filterField="deviceId"
              filterValue={trip.deviceId}
              showTabs={{
                calendar: true,
                alert: true,
                video: true,
                note: true,
              }}
              noteEntityName="trips"
              noteEntityId={detailEntityId}
              filters={tripFilters}
              hideSearch
              hideAlertEntityDetails
              hidePeriodInListHeading
              calendarSeedItems={calendarSeedItems}
              inclusiveEventDateStart
            />
            <MapWidget
              fleetId={fleetId}
              getLocationsQuery={useGetDeviceLocations}
              entityId={trip?.deviceId as string}
              entityType="trip"
              startDate={trip.startAt}
              endDate={tripEndAt}
              tripId={detailEntityId}
              tripVehicleId={trip?.vehicle?.vehicleId}
              tripSpeedMs={trip?.endAt ? undefined : trip?.speedMs}
              alerts={alerts?.results}
              alertsLoaded={alertsLoaded}
              isTrip
              isOngoingTrip={!trip?.endAt}
              mapWrapperWidth={isWideMapTab ? '50%' : '403px'}
            />
          </div>
          <DetailsPanel
            driver={getDriverInfo()}
            vehicle={tripVehicle}
            device={tripDevice}
            entity="trip"
            entityId={tripRecordId}
            allowTripDriverAssignment={!isMergedTrip}
          />
          <S.Row>
            <div className="trip-score-chart">
              <ScoreTrendWidget
                title="Trip score timeline"
                period="daily"
                data={tripScoreTrend}
              />
            </div>
            <div className="trip-score-knob">
              <KnobChartWidget
                value={tripScore !== null ? Math.round(tripScore) : 'N/A'}
                max={100}
                subtitle="Trip score"
              />
            </div>
          </S.Row>
          <S.Row>
            <S.Widget>
              <DoghnutChartWidget data={alertRisksChartConfig} />
            </S.Widget>
            <S.Widget>
              <DoghnutChartWidget data={alertTypesChartConfig} type="quad" />
            </S.Widget>
          </S.Row>
        </S.Wrapper>
      )}
      {closeTripModalOpen && !!closableTrip && (
        <Modal
          title="Close ongoing trip"
          state={closeTripModalOpen}
          settings={{ minWidth: '560px', maxWidth: '640px' }}
          toggleState={toggleCloseTripModalOpen}
        >
          <CloseTripForm
            fleetId={fleetId}
            trip={closableTrip}
            cancelLabel="Cancel"
            onCancel={() => toggleCloseTripModalOpen(false)}
            onClosed={() => toggleCloseTripModalOpen(false)}
          />
        </Modal>
      )}
    </>
  );
}

export default TripDetailsPage;
